import React from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  ShieldCheck,
} from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from '../ui/sonner';
import { cn } from '../../lib/utils';
import { driverOption } from '../../lib/selectOptions';
import { api, driversAPI, tripsAPI } from '../../lib/api';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { messageErreur } from '../../lib/erreurs';
import { LIBELLES, libelle, nomLisible, texteSur } from '../../lib/libelles';
import TeleverseurEnLigne from './TeleverseurEnLigne';

/**
 * Ce qui manque sur ce trajet, et le bouton qui le corrige ici même.
 *
 * Remplace le message rouge de quatre secondes. Un manque ne se règle pas en le
 * lisant: il se règle en téléversant une pièce, en cochant les points de
 * contrôle restants, en tapant un volume ou en choisissant un chauffeur. Tant
 * que ce n'est pas fait, le bandeau reste.
 *
 * Deux sources alimentent la liste:
 *
 *  1. l'état du trajet lui-même — pas de chauffeur, contrôle de départ;
 *  2. les refus que le serveur renvoie encore (facture sans preuve,
 *     transition impossible), captés par l'intercepteur de `lib/api.js` et
 *     relayés par l'événement DOM `ecarbu:a-reparer`.
 *
 * Les contrôles métier (commande, pièce, volume, stock, facture…) ne sont plus
 * recopiés ici: la carte « Contrôles métier » les porte, chacun avec son
 * bouton d'action. Les afficher deux fois noyait la page.
 */

const EVENEMENT_REFUS = 'ecarbu:a-reparer';

/** Le mot de l'exploitant pour un point de contrôle du camion (domaine `checklist`). */
const nomPointControle = (cle) => libelle('checklist', cle, 'Point de contrôle');

/** Les refus que le serveur peut encore renvoyer, dits à l'exploitant. */
const TITRES_REFUS = {
  INVOICE_RECONCILIATION_REQUIRED: 'Facture refusée : écart de volume non tranché',
  INVOICE_MISSION_INTEGRITY_REQUIRED: 'Facture refusée : livraison à contrôler',
  INVOICE_POD_BILLING_READY_REQUIRED: 'Facture refusée : preuve de livraison',
  INVOICE_CLIENT_POD_POLICY_REQUIRED: 'Facture refusée : preuve de livraison',
  WORKFLOW_INVALID_TRANSITION: 'Étape impossible depuis l’état actuel',
  TRIP_LOADING_STOCK_EFFECT_REQUIRED: 'Chargement refusé : mouvement de stock non enregistré',
  TRIP_ARRIVAL_STOCK_EFFECT_REQUIRED: 'Arrivée refusée : mouvement de stock non enregistré',
  TRIP_BORDER_CROSSING_STOCK_EFFECT_REQUIRED: 'Passage de frontière refusé : mouvement de stock non enregistré',
  TRIP_DIRECT_DELIVERY_STOCK_EFFECT_REQUIRED: 'Livraison directe refusée : mouvement de stock non enregistré',
};

const LIBELLES_MANQUE_SERVEUR = {
  document: 'une pièce justificative',
  valid_trip_document: 'une pièce rattachée à ce trajet',
  comment: 'un commentaire',
  location: 'une position',
  signature: 'une signature',
};

/** Les statuts où la checklist de départ a encore un sens à réparer. */
const AVANT_LE_DEPART = new Set([
  'CREATED',
  'EN_ROUTE_TO_BORDER',
  'EN_ROUTE_TO_LOADING',
  'AT_CUSTOMS_EXIT',
  'CLEARED_CUSTOMS_EXIT',
  'LOADING',
]);

export default function BandeauAReparer({
  trip,
  truck,
  organizationId,
  onRepaired,
  integre = false,
  onCount,
}) {
  const tripId = trip?.id || null;
  const [refus, setRefus] = React.useState([]);
  const [checklist, setChecklist] = React.useState(null);
  const [ouvert, setOuvert] = React.useState(null);
  // null = automatique (déplié s'il y a un blocage); un clic fixe le choix.
  const [replie, setReplie] = React.useState(null);

  // Le refus du serveur arrive par le DOM: l'intercepteur ne peut pas importer
  // ce composant sans fermer un cycle avec `lib/api.js`.
  React.useEffect(() => {
    const ecouter = (event) => {
      const detail = event?.detail;
      if (!detail) return;
      // Un refus qui nomme un autre trajet ne concerne pas cet écran.
      if (detail.trip_id && tripId && detail.trip_id !== tripId) return;
      // L'accusé de prise en charge: sans lui, l'intercepteur retombe sur le
      // toast, pour qu'un refus ne disparaisse jamais dans le vide.
      event.preventDefault();
      setRefus((precedents) => {
        const cle = `${detail.rule || ''}:${detail.operator_message || detail.message || ''}`;
        if (precedents.some((item) => item.cle === cle)) return precedents;
        return [...precedents, { ...detail, cle }];
      });
    };
    window.addEventListener(EVENEMENT_REFUS, ecouter);
    return () => window.removeEventListener(EVENEMENT_REFUS, ecouter);
  }, [tripId]);

  const chargerChecklist = React.useCallback(async () => {
    if (!tripId) return;
    try {
      const reponse = await api.get(`/driver/trips/${tripId}/departure-checklist`, {
        skipToast: true,
      });
      setChecklist(reponse.data?.data || reponse.data || null);
    } catch (error) {
      // Module workflow absent, ou trajet trop ancien: le bandeau vit sans.
      setChecklist(null);
    }
  }, [tripId]);

  React.useEffect(() => {
    chargerChecklist();
  }, [chargerChecklist]);

  const manques = React.useMemo(
    () => construireManques({ trip, checklist, refus }),
    [trip, checklist, refus],
  );

  // Intégré à la zone « À faire »: la page a besoin du nombre de points pour
  // dire « Tout est en ordre » quand il n'en reste aucun.
  const nombreManques = manques.length;
  React.useEffect(() => {
    if (onCount) onCount(nombreManques);
  }, [onCount, nombreManques]);

  const apresReparation = React.useCallback(
    async (cleManque) => {
      // Le refus qui a motivé la réparation n'a plus lieu d'être affiché: le
      // serveur le renverra s'il tient toujours.
      setRefus([]);
      setOuvert(null);
      await chargerChecklist();
      if (onRepaired) await onRepaired(cleManque);
    },
    [chargerChecklist, onRepaired],
  );

  if (!manques.length) return null;

  const reparables = manques.filter((manque) => manque.reparation).length;
  // Le chauffeur non affecté est un oubli, pas un blocage: seul, il ne déplie pas la section.
  const urgent = manques.some((manque) => manque.cle !== 'trip:driver');
  const deplie = ouvert !== null ? true : replie === null ? urgent : !replie;

  const liste = (
    <ul className={cn('divide-y', integre ? 'divide-border' : 'divide-destructive/15')} data-testid={integre ? 'bandeau-a-reparer' : undefined}>
      {manques.map((manque) => {
        const estOuvert = ouvert === manque.cle;
        return (
          <li key={manque.cle} className={cn('px-4', integre ? 'bg-card py-2.5' : 'bg-card/60 py-3')}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              {integre ? (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-flame-ink" aria-hidden="true" />
              ) : null}
              <div className="min-w-0 flex-1 basis-56">
                <p className="text-sm font-semibold text-foreground">
                  {manque.titre}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {manque.phrase}
                </p>
              </div>
              {manque.reparation ? (
                <Button
                  type="button"
                  size="sm"
                  variant={estOuvert ? 'secondary' : 'default'}
                  className={cn('shrink-0 gap-1.5', integre && 'w-full sm:w-auto')}
                  data-testid={`reparer-${manque.reparation}`}
                  onClick={() => setOuvert(estOuvert ? null : manque.cle)}
                >
                  {estOuvert ? (
                    <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {manque.libelleBouton}
                </Button>
              ) : (
                <Badge variant="outline" className="shrink-0">
                  À traiter ailleurs
                </Badge>
              )}
            </div>

            {estOuvert && (
              <div className="mt-3 rounded-lg border border-border bg-background p-3">
                <Reparation
                  manque={manque}
                  trip={trip}
                  truck={truck}
                  organizationId={organizationId}
                  checklist={checklist}
                  onDone={() => apresReparation(manque.cle)}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  // Dans la zone « À faire »: les lignes seules, sans cadre ni titre en plus.
  if (integre) return liste;

  return (
    <Collapsible
      open={deplie}
      onOpenChange={(valeur) => setReplie(!valeur)}
      asChild
    >
    <section
      data-testid="bandeau-a-reparer"
      className="overflow-hidden rounded-xl border border-destructive/40 bg-destructive/5"
      aria-live="polite"
    >
      <CollapsibleTrigger
        className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left"
        data-testid="bandeau-a-reparer-titre"
      >
        <span className="flex min-w-0 items-center gap-2">
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              deplie && 'rotate-180',
            )}
            aria-hidden="true"
          />
          <AlertTriangle
            className="h-4 w-4 shrink-0 text-destructive"
            aria-hidden="true"
          />
          <h2 className="text-sm font-semibold text-foreground">
            À corriger sur ce trajet ({manques.length})
          </h2>
        </span>
        <Badge variant="destructive" className="shrink-0">
          {manques.length} point{manques.length > 1 ? 's' : ''}
          {reparables ? ` · ${reparables} à corriger ici` : ''}
        </Badge>
      </CollapsibleTrigger>

      <CollapsibleContent className="border-t border-destructive/20">
      {liste}
      </CollapsibleContent>
    </section>
    </Collapsible>
  );
}

/* ------------------------------------------------------------------ */
/* Ce qui manque                                                       */
/* ------------------------------------------------------------------ */

export function construireManques({ trip, checklist, refus = [] }) {
  const manques = [];
  const statut = trip?.status;

  // Le chauffeur manquant n'est pas un contrôle métier: c'est un champ vide
  // que personne ne proposait de remplir, alors que le camion en porte un par
  // défaut depuis toujours.
  if (trip && !trip.driver_id) {
    manques.push({
      cle: 'trip:driver',
      titre: 'Aucun chauffeur affecté',
      phrase: "Choisissez le chauffeur qui conduit ce camion.",
      reparation: 'driver',
      libelleBouton: 'Affecter',
    });
  }

  const checklistIncomplete =
    checklist?.rule_enabled && (checklist?.blocking_reasons || []).length > 0;
  if (checklistIncomplete && AVANT_LE_DEPART.has(statut)) {
    manques.push({
      cle: 'trip:checklist',
      titre: 'Contrôle de départ incomplet',
      phrase:
        'Le trajet est parti sans contrôle de départ complet: cochez les points de contrôle restants.',
      reparation: 'checklist',
      libelleBouton: 'Cocher les points',
    });
  }

  for (const item of refus) {
    const libelles = (item.missing || [])
      .map((cle) => LIBELLES_MANQUE_SERVEUR[cle] || 'une information')
      .join(', ');
    manques.push({
      cle: `refus:${item.cle}`,
      titre: item.rule ? TITRES_REFUS[item.rule] || 'Action refusée' : 'Action refusée',
      phrase: [texteSur(item.operator_message, ''), libelles && `Il manque : ${libelles}.`]
        .filter(Boolean)
        .join(' '),
      reparation: (item.missing || []).some((cle) =>
        ['document', 'valid_trip_document', 'signature'].includes(cle),
      )
        ? 'document'
        : null,
      libelleBouton: 'Téléverser',
    });
  }

  return manques;
}

/* ------------------------------------------------------------------ */
/* Les quatre gestes qui réparent                                      */
/* ------------------------------------------------------------------ */

function Reparation({ manque, trip, truck, organizationId, checklist, onDone }) {
  if (manque.reparation === 'document') {
    return <TeleverseurEnLigne tripId={trip?.id} onUploaded={onDone} />;
  }
  if (manque.reparation === 'volume') {
    return <SaisieVolume trip={trip} onDone={onDone} />;
  }
  if (manque.reparation === 'driver') {
    return (
      <ChoixChauffeur
        trip={trip}
        truck={truck}
        organizationId={organizationId}
        onDone={onDone}
      />
    );
  }
  if (manque.reparation === 'checklist') {
    return <ChecklistDepart trip={trip} checklist={checklist} onDone={onDone} />;
  }
  return null;
}

function SaisieVolume({ trip, onDone }) {
  const [valeur, setValeur] = React.useState(
    trip?.fuel_quantity_real ?? trip?.fuel_quantity_estimated ?? '',
  );
  const [enCours, setEnCours] = React.useState(false);

  const enregistrer = async () => {
    const litres = Number.parseFloat(valeur);
    if (!Number.isFinite(litres) || litres <= 0) {
      toast.error('Saisissez un volume en litres.');
      return;
    }
    setEnCours(true);
    try {
      await tripsAPI.update(trip.id, { fuel_quantity_real: litres });
      toast.success('Volume enregistré.');
      await onDone();
    } catch (error) {
      toast.error(messageErreur(error, 'Le volume n’a pas pu être enregistré.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground">
        Volume réellement chargé ou livré (en litres)
      </Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          value={valeur}
          onChange={(event) => setValeur(event.target.value)}
          placeholder="Ex. 38000"
          data-testid="reparation-volume-input"
          className="sm:w-56"
        />
        <Button type="button" onClick={enregistrer} disabled={enCours} className="gap-2">
          {enCours ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Check className="h-4 w-4" aria-hidden="true" />
          )}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

function ChoixChauffeur({ trip, truck, organizationId, onDone }) {
  const [chauffeurs, setChauffeurs] = React.useState([]);
  const [choisi, setChoisi] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);

  // Le camion porte déjà le chauffeur habituel de la société, et rien ne le
  // proposait: le dispatcher le retapait de mémoire à chaque trajet.
  const chauffeurParDefaut = React.useMemo(
    () =>
      truck?.default_driver_id ||
      (truck?.org_overrides || {})[organizationId]?.default_driver_id ||
      '',
    [truck, organizationId],
  );

  React.useEffect(() => {
    let annule = false;
    fetchAllPages((page) => driversAPI.getAll(page))
      .then((resultat) => {
        if (!annule) setChauffeurs(resultat.items);
      })
      .catch(() => {
        if (!annule) setChauffeurs([]);
      });
    return () => {
      annule = true;
    };
  }, []);

  React.useEffect(() => {
    if (chauffeurParDefaut) setChoisi(chauffeurParDefaut);
  }, [chauffeurParDefaut]);

  const optionsChauffeurs = React.useMemo(
    () =>
      chauffeurs.map((chauffeur) => ({
        ...driverOption(chauffeur),
        label: `${nomLisible(chauffeur.full_name, chauffeur.name, 'Chauffeur sans nom')}${
          chauffeur.id === chauffeurParDefaut ? ' (habituel)' : ''
        }`,
      })),
    [chauffeurs, chauffeurParDefaut],
  );

  const affecter = async () => {
    if (!choisi) {
      toast.error('Choisissez un chauffeur.');
      return;
    }
    setEnCours(true);
    try {
      await tripsAPI.update(trip.id, { driver_id: choisi });
      toast.success('Chauffeur affecté.');
      await onDone();
    } catch (error) {
      toast.error(messageErreur(error, 'Le chauffeur n’a pas pu être affecté.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground">
        Chauffeur du trajet
        {chauffeurParDefaut ? ' — le chauffeur habituel de ce camion est proposé' : ''}
      </Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <SearchableSelect
          className="sm:w-72"
          data-testid="reparation-driver-select"
          placeholder="Choisir un chauffeur"
          searchPlaceholder="Rechercher un chauffeur…"
          emptyMessage="Aucun chauffeur trouvé"
          value={choisi || ''}
          onValueChange={setChoisi}
          options={optionsChauffeurs}
        />
        <Button type="button" onClick={affecter} disabled={enCours} className="gap-2">
          {enCours ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Check className="h-4 w-4" aria-hidden="true" />
          )}
          Affecter
        </Button>
      </div>
    </div>
  );
}

function ChecklistDepart({ trip, checklist, onDone }) {
  const requis = React.useMemo(
    () => checklist?.required_critical_items || Object.keys(LIBELLES.checklist),
    [checklist],
  );

  // Ce qui a déjà été coché — depuis le téléphone du chauffeur, le plus
  // souvent — ne se recoche pas: le bureau termine le travail commencé.
  const dejaCoches = React.useMemo(() => {
    const items = checklist?.latest?.items || [];
    return new Set(
      items
        .filter((item) => item?.checked)
        .map((item) => String(item.key || '').toUpperCase()),
    );
  }, [checklist]);

  const [coches, setCoches] = React.useState(() => new Set(dejaCoches));
  const [enCours, setEnCours] = React.useState(false);

  React.useEffect(() => {
    setCoches(new Set(dejaCoches));
  }, [dejaCoches]);

  const basculer = (cle) => {
    setCoches((precedent) => {
      const suivant = new Set(precedent);
      if (suivant.has(cle)) suivant.delete(cle);
      else suivant.add(cle);
      return suivant;
    });
  };

  const restants = requis.filter((cle) => !coches.has(cle));

  const valider = async () => {
    setEnCours(true);
    try {
      await api.post(`/driver/trips/${trip.id}/departure-checklist`, {
        completed_at: new Date().toISOString(),
        note: 'Contrôle de départ complété depuis le bureau',
        items: requis.map((cle) => ({
          key: cle,
          label: nomPointControle(cle),
          critical: true,
          checked: coches.has(cle),
        })),
      });
      toast.success(
        restants.length
          ? 'Contrôle enregistré — il reste des points non cochés.'
          : 'Contrôle de départ complet.',
      );
      await onDone();
    } catch (error) {
      toast.error(messageErreur(error, 'Le contrôle de départ n’a pas pu être enregistré.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-xs font-medium text-muted-foreground">
        Points de contrôle du départ
      </Label>
      <ul className="grid gap-1.5 sm:grid-cols-2">
        {requis.map((cle) => {
          const coche = coches.has(cle);
          return (
            <li key={cle}>
              <button
                type="button"
                onClick={() => basculer(cle)}
                aria-pressed={coche}
                data-testid={`checklist-${cle}`}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                  coche
                    ? 'border-success/40 bg-success/10 text-foreground'
                    : 'border-border bg-card hover:bg-muted',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    coche ? 'border-success bg-success text-white' : 'border-border',
                  )}
                  aria-hidden="true"
                >
                  {coche ? <Check className="h-3 w-3" /> : null}
                </span>
                {nomPointControle(cle)}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={valider} disabled={enCours} className="gap-2">
          {enCours ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          )}
          Valider le contrôle
        </Button>
        <span className="text-xs text-muted-foreground">
          {restants.length
            ? `${restants.length} point(s) encore non coché(s)`
            : 'Tous les points sont cochés'}
        </span>
      </div>
    </div>
  );
}
